import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, RotateCcw, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { FeaturedCouplesAdmin } from "../../components/home/FeaturedCouplesAdmin";
import { FocusPicker } from "../../components/site/FocusPicker";

const BLANK = { image_url: "", eyebrow: "", headline: "New slide headline", accent: "", subhead: "", cta_label: "Get started", cta_link: "/signup" };
const src = (s) => s.image_data_url || (s.image_url?.startsWith("/api/") ? `${BACKEND_URL}${s.image_url}` : s.image_url);

function Field({ label, value, onChange, testid, area }) {
  const C = area ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</span>
      <C data-testid={testid} value={value || ""} rows={area ? 2 : undefined} onChange={(e) => onChange(e.target.value)} className="input-luxe resize-none" />
    </label>
  );
}

function SlideCard({ s, idx, total, update, move, remove }) {
  function pick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    const r = new FileReader();
    r.onload = () => update({ image_data_url: r.result });
    r.readAsDataURL(f);
  }
  return (
    <div data-testid={`admin-editor-slide-item-${idx}`} className="bg-white border border-[var(--line)] rounded-2xl p-4 md:p-5 grid md:grid-cols-[220px_1fr] gap-5">
      <div>
        <div className="aspect-[4/3] rounded-xl bg-[var(--bg-warm)] overflow-hidden">{src(s) && <img src={src(s)} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${s.focus_x ?? 50}% ${s.focus_y ?? 50}%` }} />}</div>
        <label className="btn-outline-gold text-[11px] !py-2 w-full mt-3 cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> Upload image
          <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`admin-editor-slide-upload-${idx}`} />
        </label>
        <div className="flex gap-1 mt-3 justify-center">
          <button disabled={idx === 0} onClick={() => move(-1)} data-testid={`admin-editor-slide-up-${idx}`} className="p-2 rounded-full hover:bg-black/5 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
          <button disabled={idx === total - 1} onClick={() => move(1)} data-testid={`admin-editor-slide-down-${idx}`} className="p-2 rounded-full hover:bg-black/5 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
          <button disabled={total === 1} onClick={remove} data-testid={`admin-editor-slide-delete-${idx}`} className="p-2 rounded-full hover:bg-red-50 text-red-600 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {src(s) && <div className="sm:col-span-2 rounded-xl bg-[var(--bg-warm)] p-3"><FocusPicker src={src(s)} focus={{ x: s.focus_x ?? 50, y: s.focus_y ?? 50 }} onChange={(f) => update({ focus_x: f.x, focus_y: f.y })} testid={`admin-slide-focus-${idx}`} /></div>}
        <div className="sm:col-span-2"><Field label="Image URL (or upload)" value={s.image_data_url ? "(new upload)" : s.image_url} onChange={(v) => update({ image_url: v, image_data_url: null })} testid={`admin-editor-slide-image-${idx}`} /></div>
        <Field label="Eyebrow" value={s.eyebrow} onChange={(v) => update({ eyebrow: v })} testid={`admin-editor-slide-eyebrow-${idx}`} />
        <Field label="Headline" value={s.headline} onChange={(v) => update({ headline: v })} testid={`admin-editor-slide-headline-${idx}`} />
        <Field label="Gold script accent" value={s.accent} onChange={(v) => update({ accent: v })} testid={`admin-editor-slide-accent-${idx}`} />
        <Field label="Button label" value={s.cta_label} onChange={(v) => update({ cta_label: v })} testid={`admin-editor-slide-cta-label-${idx}`} />
        <div className="sm:col-span-2"><Field area label="Subhead" value={s.subhead} onChange={(v) => update({ subhead: v })} testid={`admin-editor-slide-subhead-${idx}`} /></div>
        <div className="sm:col-span-2"><Field label="Button link" value={s.cta_link} onChange={(v) => update({ cta_link: v })} testid={`admin-editor-slide-cta-link-${idx}`} /></div>
      </div>
    </div>
  );
}

export default function AdminHomepagePage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site/homepage").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <p className="text-sm text-[var(--muted)]">Loading…</p>;

  const setSlides = (slides) => setCfg({ ...cfg, slides });
  const update = (i, patch) => setSlides(cfg.slides.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const move = (i, d) => { const a = [...cfg.slides]; [a[i], a[i + d]] = [a[i + d], a[i]]; setSlides(a); };

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/site/homepage", { theme: cfg.theme, autoplay_seconds: cfg.autoplay_seconds, slides: cfg.slides });
      setCfg(data);
      toast.success("Homepage published");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function reset() {
    if (!window.confirm("Reset the homepage hero to the default slides?")) return;
    const { data } = await api.delete("/admin/site/homepage");
    setCfg(data);
    toast.success("Reset to defaults");
  }

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-homepage-editor-container">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Homepage</p>
          <h1 className="font-serif font-light text-4xl">Hero & theme</h1>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="btn-outline-gold text-xs !py-2.5" data-testid="admin-editor-preview-btn"><ExternalLink className="w-3.5 h-3.5" /> Preview</a>
          <button onClick={reset} className="btn-outline-gold text-xs !py-2.5" data-testid="admin-editor-reset-btn"><RotateCcw className="w-3.5 h-3.5" /> Defaults</button>
          <button onClick={save} disabled={busy} className="btn-gold text-xs !py-2.5" data-testid="admin-editor-save-btn"><Save className="w-3.5 h-3.5" /> {busy ? "Publishing…" : "Publish"}</button>
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-5 grid sm:grid-cols-2 gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mb-2">Homepage mood</p>
          <div className="flex gap-2" data-testid="admin-editor-theme-select">
            {[["noir", "Black & gold"], ["ivory", "Ivory & champagne"]].map(([v, l]) => (
              <button key={v} onClick={() => setCfg({ ...cfg, theme: v })} data-testid={`admin-editor-theme-${v}`}
                className={`flex-1 rounded-xl border p-3 text-left transition-colors ${cfg.theme === v ? "border-[var(--gold)] ring-2 ring-[#C8A96B]/30" : "border-[var(--line)]"}`}>
                <span className={`block h-8 rounded-lg mb-2 ${v === "noir" ? "bg-[#0B0B0B]" : "bg-[#F3EEE5]"}`}><span className="block h-full w-1/3 rounded-lg bg-[#C8A96B]/80" /></span>
                <span className="text-sm">{l}</span>
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Seconds per slide ({cfg.autoplay_seconds}s)</span>
          <input type="range" min={3} max={15} value={cfg.autoplay_seconds} onChange={(e) => setCfg({ ...cfg, autoplay_seconds: +e.target.value })} className="w-full mt-4 accent-[var(--gold)]" data-testid="admin-editor-autoplay" />
        </label>
      </div>

      <div className="space-y-4">
        {cfg.slides.map((s, i) => (
          <SlideCard key={s.id || i} s={s} idx={i} total={cfg.slides.length} update={(p) => update(i, p)} move={(d) => move(i, d)} remove={() => setSlides(cfg.slides.filter((_, k) => k !== i))} />
        ))}
      </div>
      {cfg.slides.length < 8 && (
        <button onClick={() => setSlides([...cfg.slides, { ...BLANK }])} className="btn-outline-gold w-full" data-testid="admin-editor-add-slide-btn"><Plus className="w-4 h-4" /> Add slide</button>
      )}
      <FeaturedCouplesAdmin />
    </div>
  );
}
